import { Box, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectListFields, selectListSettingsState } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks.ts";
import { isDimension } from "../../../../utilities/fieldsHelper.ts";
import SwitchField from "../../../common/SwitchField.tsx";
import { getColumnOptions } from "../../common/blankFieldSettings.ts";
import ColumnsAutocomplete from "../../common/ColumnsAutocomplete.tsx";
import MeasureSettings from "../../tabular/settings/MeasureSettings.tsx";

interface Props {
  readonlyMode: boolean;
}

const ListSettingsContainer = ({ readonlyMode }: Props) => {
  const settings = useSelector(selectListSettingsState);
  const fields = useSelector(selectListFields);
  const dispatch = useAppDispatch();

  const dimensionFields = fields.filter(isDimension);
  const columnOptions = getColumnOptions(
    dimensionFields,
    dimensionFields.map((field) => field.config.guid)
  );
  const selectedColumns = getColumnOptions(dimensionFields, settings.hideBlankRowsColumnIds);

  return (
    <ScrollableFlexContainer>
      <Stack spacing={2} useFlexGap sx={{ px: "1.3rem", width: "100%" }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1">General</Typography>
          <Stack sx={{ gap: 0.7 }}>
            <SwitchField
              label="Show Grand Total"
              checked={settings.showGrandTotal === true}
              onChange={(value) => dispatch(fieldsStateActions.updateSettings({ settings: { showGrandTotal: value } }))}
              disabled={readonlyMode}
            />
            <SwitchField
              label="Hide Rows if Columns Are Empty"
              checked={settings.hideBlankRows}
              onChange={(hideBlankRows) =>
                dispatch(
                  fieldsStateActions.updateSettings({
                    settings: {
                      hideBlankRows,
                      hideBlankRowsColumnIds: hideBlankRows ? settings.hideBlankRowsColumnIds : [],
                    },
                  })
                )
              }
              tooltipText="Hides rows in which all selected columns are empty."
              disabled={readonlyMode}
            />
            {settings.hideBlankRows && (
              <Box sx={{ ml: 1.25, my: 1 }}>
                <ColumnsAutocomplete
                  options={columnOptions}
                  value={selectedColumns}
                  disabled={readonlyMode}
                  onChange={(hideBlankRowsColumnIds) =>
                    dispatch(fieldsStateActions.updateSettings({ settings: { hideBlankRowsColumnIds } }))
                  }
                />
              </Box>
            )}
          </Stack>
        </Stack>
        <MeasureSettings
          title="Numeric Columns"
          readonlyMode={readonlyMode}
          includeNumericDimensions
          switchLabel="Calculate Grand Total"
        />
      </Stack>
    </ScrollableFlexContainer>
  );
};

export default ListSettingsContainer;
